import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faChartLine } from '@fortawesome/free-solid-svg-icons';
import About from '../components/About';
import CoreValues from '../components/CoreValues';
import SEO from '../components/SEO';

// Import client logos
import logo3DHacks from '../assets/clients/3D-Hacks.webp';
import logoBB from '../assets/clients/BB logo.webp';
import logoMadInd from '../assets/clients/Mad Ind insta - Copy.webp';
import logoPhotoroom from '../assets/clients/Photoroom-20240217_184346 - Copy.webp';
import logoGiftRealm from '../assets/clients/gift-realm.png';
import logoS from '../assets/clients/logo-s.webp';
import logo from '../assets/clients/logo.webp';
import logoUnlimitedMelodies from '../assets/clients/unlimited Melodies.webp';
import AboutUsBanner from "../assets/about-bg.webp"

const AboutPage = () => {
  return (
    <>
      <SEO 
        title="About Us" 
        description="Learn about our company's journey, mission, vision, and core values. From humble beginnings to industry leadership, discover the story behind our success."
        keywords="company history, mission, vision, core values, digital transformation, innovation"
      />
      {/* Company History Section */}
      <HistorySection backgroundImage={AboutUsBanner}>
        <Container>
          <SectionTitle>
            <SubTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              OUR JOURNEY
            </SubTitle>
            <Title
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              The <span>Story</span> Behind Our Success
            </Title>
            <Description
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              From humble beginnings to industry leadership, our journey has been defined by innovation, perseverance, and a commitment to excellence.
            </Description>
          </SectionTitle>
          
          <TimelineWrapper>
            {timelineData.map((item, index) => (
              <TimelineItem 
                key={item.id}
                className={index % 2 === 0 ? 'left' : 'right'}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
              >
                <TimelineContent>
                  <TimelineYear>{item.year}</TimelineYear>
                  <TimelineTitle>{item.title}</TimelineTitle>
                  <TimelineText>{item.description}</TimelineText>
                </TimelineContent>
              </TimelineItem>
            ))}
          </TimelineWrapper>
        </Container>
      </HistorySection>
      
      {/* About Section */}
      <About />
      
      {/* Mission & Vision Section */}
      <MissionSection>
        <Container>
          <MissionWrapper>
            <MissionItem
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <MissionIcon>
                <FontAwesomeIcon icon={faLightbulb} />
              </MissionIcon>
              <MissionTitle>Our Mission</MissionTitle>
              <MissionText>
                To empower businesses with innovative digital solutions that drive growth, enhance customer experiences, and create lasting value. We are committed to delivering excellence through creativity, technical expertise, and strategic thinking.
              </MissionText>
            </MissionItem>
            
            <MissionItem
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <MissionIcon>
                <FontAwesomeIcon icon={faChartLine} />
              </MissionIcon>
              <MissionTitle>Our Vision</MissionTitle>
              <MissionText>
                To be the leading digital transformation partner for businesses worldwide, recognized for our innovative solutions, exceptional service, and positive impact on clients' success. We envision a future where technology seamlessly enhances human experiences and drives sustainable growth.
              </MissionText>
            </MissionItem>
          </MissionWrapper>
        </Container>
      </MissionSection>
      
      {/* Core Values Section */}
      <CoreValues />
      
      {/* Clients Section */}
      <ClientsSection>
        <Container>
          <SectionTitle>
            <SubTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              OUR CLIENTS
            </SubTitle>
            <Title
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{ color: '#333' }}
            >
              Trusted by <span>Leading</span> Companies
            </Title>
            <Description
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              style={{ color: '#333' }}
            >
              We're proud to work with these amazing organizations who trust us with their digital needs.
            </Description>
          </SectionTitle>
          
          <ClientCarousel>
            <CarouselTrack>
              <CarouselSlide>
                <ClientLogo src={logo3DHacks} alt="3D-Hacks" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logoBB} alt="BB Logo" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logoMadInd} alt="Mad Ind" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logoPhotoroom} alt="Photoroom" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logoGiftRealm} alt="Gift Realm" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logoS} alt="Logo S" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logo} alt="Logo" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logoUnlimitedMelodies} alt="Unlimited Melodies" />
              </CarouselSlide>
              {/* Duplicate slides for infinite effect */}
              <CarouselSlide>
                <ClientLogo src={logo3DHacks} alt="3D-Hacks" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logoBB} alt="BB Logo" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logoMadInd} alt="Mad Ind" />
              </CarouselSlide>
              <CarouselSlide>
                <ClientLogo src={logoPhotoroom} alt="Photoroom" />
              </CarouselSlide>
            </CarouselTrack>
          </ClientCarousel>
        </Container>
      </ClientsSection>
    </>
  );
};

// Styled Components
const HistorySection = styled.section`
  padding: 100px 0;
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${props => props.backgroundImage});
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SubTitle = styled(motion.span)`
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  color: #ff6e6e;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 50%;
    width: 30px;
    height: 1px;
    background-color: #ff6e6e;
  }
  
  &:before {
    left: -40px;
  }
  
  &:after {
    right: -40px;
  }
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 20px;
  
  span {
    color: #ff6e6e;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  color: #f3f3f3;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TimelineWrapper = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  
  &:after {
    content: '';
    position: absolute;
    width: 4px;
    background-color: #ff6e6e;
    top: 0;
    bottom: 0;
    left: 50%;
    margin-left: -2px;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    &:after {
      left: 31px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  padding: 10px 40px;
  position: relative;
  width: 50%;
  box-sizing: border-box;
  margin-bottom: 30px;
  
  &:after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    right: -10px;
    background-color: white;
    border: 4px solid #ff6e6e;
    top: 15px;
    border-radius: 50%;
    z-index: 1;
  }
  
  &.left {
    left: 0;
  }
  
  &.right {
    left: 50%;
  }
  
  &.right:after {
    left: -10px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding-left: 70px;
    padding-right: 25px;
    
    &.left:after, &.right:after {
      left: 21px;
    }
    
    &.right {
      left: 0%;
    }
  }
`;

const TimelineContent = styled.div`
  padding: 20px 30px;
  background-color: white;
  position: relative;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  
  &:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
  }
  
  transition: all 0.3s ease;
`;

const TimelineYear = styled.span`
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background-color: #ff6e6e;
  padding: 5px 15px;
  border-radius: 20px;
  margin-bottom: 10px;
`;

const TimelineTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
`;

const TimelineText = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const MissionSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
`;

const MissionWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MissionItem = styled(motion.div)`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const MissionIcon = styled.div`
  width: 80px;
  height: 80px;
  background-color: rgba(255, 94, 20, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  color: #ff6e6e;
  font-size: 32px;
`;

const MissionTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
`;

const MissionText = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.8;
`;

const ClientsSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
`;

const ClientCarousel = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  margin-top: 50px;
`;

const CarouselTrack = styled.div`
  display: flex;
  animation: scroll 30s linear infinite;
  width: calc(250px * 16); /* Adjust based on number of slides */
  
  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(calc(-250px * 8)); /* Half the total width */
    }
  }
`;

const CarouselSlide = styled.div`
  width: 250px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
`;

const ClientLogo = styled.img`
  max-width: 100%;
  max-height: 100px;
  object-fit: contain;
  opacity: 0.7;
  transition: all 0.3s ease;
  
  &:hover {
    filter: grayscale(0%);
    opacity: 1;
  }
`;

// Timeline Data
const timelineData = [
  {
    id: 1,
    year: '2018',
    title: 'Company Founded',
    description: 'Our company was founded with a vision to create innovative digital solutions for businesses of all sizes.'
  },
  {
    id: 2,
    year: '2020',
    title: 'Expanded Services',
    description: 'We expanded our service offerings to include UI/UX design, mobile app development, and digital marketing solutions.'
  },
  {
    id: 3,
    year: '2022',
    title: 'International Expansion',
    description: 'Established global infrastructure with strategic data centers in India, Germany, and Singapore to enhance service delivery and support our international client base.'
  },
  {
    id: 4,
    year: '2025',
    title: 'Cloud Support Expansion',
    description: 'Expanded our service offerings to include comprehensive cloud support solutions, helping businesses optimize their cloud infrastructure and operations.'
  }
];

export default AboutPage;